import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";
import { LeavesReviewResponse } from "views/LeavesReview/types";
import { logoPdf } from "config/constants";

const styles = StyleSheet.create({
  page: { padding: 36, fontSize: 11, fontFamily: "Helvetica" },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  logo: { width: 160, height: 48, objectFit: "contain" },
  mainTitle: {
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
    marginTop: 8,
    marginBottom: 24,
    textTransform: "uppercase",
  },
  block: { marginBottom: 28 },
  blockTitle: {
    fontSize: 11,
    fontWeight: "bold",
    textTransform: "uppercase",
    marginBottom: 6,
  },
  infoLine: { fontSize: 10, marginBottom: 2 },
  sectionHeading: {
    fontSize: 11,
    fontWeight: "bold",
    marginTop: 18,
    marginBottom: 10,
    textTransform: "uppercase",
  },
  rowsWrapper: { marginBottom: 6 },
  row: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    fontSize: 10,
    lineHeight: 1.3,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  rowLabel: { fontWeight: "normal" },
  rowValue: { fontWeight: "bold" },
  note: { fontSize: 8, color: "#555", marginTop: 32, lineHeight: 1.2 },
  totalRow: { backgroundColor: "#d4d4d4" },
  striped: { backgroundColor: "#f5f5f5" },
});

export interface LeavesSummaryPDFProps {
  data: LeavesReviewResponse;
  selectedDate: Date | string | null;
  userFullName?: string;
  userRut?: string;
}

const formatDate = (date?: string | null) =>
  date ? dayjs(date).format("DD/MM/YYYY") : "-";

export const LeavesSummaryPDF = ({
  data,
  selectedDate,
  userFullName,
  userRut,
}: LeavesSummaryPDFProps) => {
  const dateForTitle = selectedDate
    ? formatDate(
        typeof selectedDate === "string"
          ? selectedDate
          : dayjs(selectedDate).format("YYYY-MM-DD")
      )
    : formatDate();
  const { holidaysStatus, permissionsStatus } = data;

  const vacationRows: { label: string; value: any; isTotal?: boolean }[] = [
    {
      label: "Días acumulados período anterior",
      value: holidaysStatus?.p_acumulados,
    },
    {
      label: "Días acumulados período actual",
      value: holidaysStatus?.p_periodo,
    },
    { label: "Días progresivos", value: holidaysStatus?.p_progresivo },
    { label: "Días transferidos", value: holidaysStatus?.p_transferidos },
    {
      label: "Días utilizados en período actual",
      value: holidaysStatus?.p_tomados_periodo,
    },
    {
      label: "Días interrumpidos",
      value: holidaysStatus?.p_interrumpidos,
    },
    {
      label: "Días adelantados",
      value: holidaysStatus?.p_adelantados ?? 0,
    },
    {
      label: "Total días disponibles",
      value: holidaysStatus?.p_disponibles,
      isTotal: true,
    },
  ];

  const adminRows: { label: string; value: any; isTotal?: boolean }[] = [
    {
      label: "Total días disponibles",
      value: permissionsStatus?.p_disponibles,
      isTotal: true,
    },
  ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={logoPdf} style={styles.logo} />
          <Text
            style={{ fontSize: 9, color: "#444" }}
          >{`Generado: ${dayjs().format("DD/MM/YYYY")}`}</Text>
        </View>
        <Text style={styles.mainTitle}>RESUMEN DE PERMISOS Y VACACIONES</Text>

        <View style={styles.block}>
          <Text style={styles.blockTitle}>
            Resumen de vacaciones disponibles
          </Text>
          {userFullName && (
            <Text
              style={styles.infoLine}
            >{`Funcionario: ${userFullName}`}</Text>
          )}
          {userRut && <Text style={styles.infoLine}>{`ID: ${userRut}`}</Text>}
        </View>

        <Text
          style={styles.sectionHeading}
        >{`DÍAS DE VACACIONES A LA FECHA ${dateForTitle}`}</Text>
        <View style={styles.rowsWrapper}>
          {vacationRows.map((r, idx) => {
            const rowStyle: any = {
              ...styles.row,
              ...(r.isTotal ? styles.totalRow : {}),
              ...(!r.isTotal && idx % 2 === 1 ? styles.striped : {}),
            };
            return (
              <View key={r.label} style={rowStyle}>
                <Text style={styles.rowLabel}>{`${r.label}: `}</Text>
                <Text style={styles.rowValue}>{r.value ?? "-"} </Text>
              </View>
            );
          })}
        </View>

        <Text
          style={styles.sectionHeading}
        >{`DÍAS ADMINISTRATIVOS A LA FECHA ${dateForTitle}`}</Text>
        <View style={styles.rowsWrapper}>
          {adminRows.map((r, idx) => {
            const rowStyle: any = {
              ...styles.row,
              ...(r.isTotal ? styles.totalRow : {}),
              ...(!r.isTotal && idx % 2 === 1 ? styles.striped : {}),
            };
            return (
              <View key={r.label} style={rowStyle}>
                <Text style={styles.rowLabel}>{`${r.label}: `}</Text>
                <Text style={styles.rowValue}>{r.value ?? "-"} </Text>
              </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );
};
export default LeavesSummaryPDF;
